<script setup lang="ts">
import moment from "moment";
import { computed, onUnmounted, ref, watch } from "vue";

import Avatar from "~/components/dumb/Avatar.vue";
import EntityPreviewTooltip from "~/components/dumb/EntityPreviewTooltip.vue";
import UserLink from "~/components/dumb/UserLink.vue";
import TimeTrackingEntry from "~/components/timeTracking/TimeTrackingEntry.vue";
import { ChevronRightIcon } from "~/icons";
import type { TimeTracking, TimeTrackingEntry as ITimeTrackingEntry, User } from "~/shared/types";
import { getActiveTotalTimeElapsedText, useTimeTracker } from "~/utils/time";
import { getUserDisplayName } from "~/utils/user";

const { user, timeTracking } = defineProps<{
  user: User;
  timeTracking: TimeTracking;
}>();

const emit = defineEmits<{
  update: [current: ITimeTrackingEntry, updated: ITimeTrackingEntry];
  delete: [entry: ITimeTrackingEntry];
}>();

const expanded = ref(false);

const activeTimeEntry = computed(() => timeTracking.find((entry) => !entry.finishedAt));
const { now, startTimer, stopTimer } = useTimeTracker(activeTimeEntry);

const activeTimeElapsedDuration = computed(() => {
  if (!activeTimeEntry.value) {
    return moment.duration(0);
  }
  return moment.duration(moment(now.value).diff(moment(activeTimeEntry.value.startedAt)));
});

const totalTimeTrackingText = computed(() =>
  getActiveTotalTimeElapsedText(timeTracking, activeTimeElapsedDuration.value)
);

const onClick = (e: Event) => e.stopImmediatePropagation();

watch(
  () => activeTimeEntry.value,
  (newValue) => {
    if (!newValue) {
      return;
    }
    startTimer();
  },
  { immediate: true }
);

onUnmounted(() => {
  stopTimer();
});
</script>
<template>
  <div class="flex w-full flex-col items-center py-1">
    <div
      class="group/user-summary flex w-full cursor-pointer items-center gap-2 px-3 py-1 select-none hover:bg-md"
      @click="expanded = !expanded"
      @keydown.enter="expanded = !expanded">
      <EntityPreviewTooltip :user-duid="user.duid" class="mr-0.5 size-6 group-hover/user-summary:hidden">
        <Avatar :user="user" class="size-6" />
      </EntityPreviewTooltip>

      <span
        class="mr-0.5 hidden size-6 items-center justify-center rounded-sm group-hover/user-summary:flex hover:bg-hvy">
        <ChevronRightIcon class="icon-md text-lt transition-transform" :class="expanded && 'rotate-90'" />
      </span>

      <EntityPreviewTooltip :user-duid="user.duid" class="flex-1">
        <UserLink :user="user" class="text-sm text-lt">
          {{ getUserDisplayName(user) }}
        </UserLink>
      </EntityPreviewTooltip>

      <span class="justify-end text-sm text-lt tabular-nums">
        {{ totalTimeTrackingText }}
      </span>
    </div>

    <div
      v-if="expanded"
      class="flex size-full flex-col gap-2 pb-1 hover:bg-lt"
      @click="onClick"
      @keydown.enter="onClick">
      <div class="pointer-events-auto flex flex-col">
        <table class="w-full table-auto border-collapse">
          <TimeTrackingEntry
            v-for="entry in timeTracking"
            :key="entry.startedAt"
            :entry="entry"
            @update="(current, updated) => emit('update', current, updated)"
            @delete="(entry) => emit('delete', entry)" />
        </table>
      </div>
    </div>
  </div>
</template>
